import React from 'react';
import { Pressable } from 'react-native';

import { Stack } from '@/components/layout';
import { SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';
import type { ProfileAction } from '../types';

type Props = {
  title: string;
  actions: ProfileAction[];
  selectedKey?: string;
  onSelect?: (action: ProfileAction) => void;
  tablet?: boolean;
};

export function ProfileActionSection({
  title,
  actions,
  selectedKey,
  onSelect,
  tablet = false,
}: Props) {
  const { scale } = useScaling();
  const styles = useStyles();

  return (
    <Stack gap={scale(12)}>
      <Text variant="Font Guardian App/Body/Body 16 Bold" style={styles.title}>
        {title}
      </Text>
      <Stack
        style={[
          styles.card,
          tablet && title === STRINGS.guardianProfile.settings.title
            ? styles.tabletSettingsCard
            : null,
          tablet && title === STRINGS.guardianProfile.productOrders ? styles.tabletOrderCard : null,
        ]}>
        {actions.map((action, index) => {
          const selected = action.key === selectedKey;
          return (
            <React.Fragment key={action.key}>
              <Pressable
                accessibilityRole="button"
                onPress={() => {
                  onSelect?.(action);
                  action.onPress?.();
                }}
                style={({ pressed }) => [
                  styles.row,
                  tablet && title === STRINGS.guardianProfile.settings.title
                    ? styles.tabletSettingsRow
                    : null,
                  selected ? (tablet ? styles.tabletSelectedRow : styles.selectedRow) : null,
                  pressed ? styles.pressed : null,
                ]}>
                <SvgIcon
                  name={action.icon}
                  size={scale(24)}
                  color={
                    selected && tablet
                      ? guardianColors['Main/Main']
                      : selected
                        ? colors['0']
                        : guardianColors['6']
                  }
                />
                <Text
                  variant={
                    tablet
                      ? selected
                        ? 'Font Guardian App/Subtitle/Subtitle 14 Bold'
                        : 'Font Guardian App/Subtitle/Subtitle 14 Regular'
                      : 'Font Guardian App/Subtitle/Subtitle 14 Semibold'
                  }
                  style={[
                    styles.label,
                    selected ? (tablet ? styles.tabletSelectedLabel : styles.selectedLabel) : null,
                  ]}>
                  {action.label}
                </Text>
                {title !== STRINGS.guardianProfile.settings.title || !tablet ? (
                  <SvgIcon name="IcChevronRight" size={scale(20)} color={guardianColors['6']} />
                ) : null}
              </Pressable>
              {title === STRINGS.guardianProfile.settings.title &&
              index < actions.length - 1 &&
              (!tablet || !selected) ? (
                <Stack style={styles.divider} />
              ) : null}
            </React.Fragment>
          );
        })}
      </Stack>
    </Stack>
  );
}

const useStyles = createStyles(({ scale }) => ({
  title: { color: guardianColors['10'] },
  card: {
    overflow: 'hidden',
    padding: scale(12),
    gap: scale(12),
    borderRadius: scale(12),
    borderCurve: 'continuous',
    backgroundColor: colors['0'],
    shadowColor: '#222B45',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.04,
    shadowRadius: scale(8),
    elevation: 1,
  },
  row: {
    height: scale(32),
    flexDirection: 'row',
    alignItems: 'center',
    gap: scale(8),
  },
  divider: { height: 0, borderTopWidth: 1, borderTopColor: guardianColors['3'] },
  label: { flex: 1, color: guardianColors['10'] },
  selectedRow: {
    borderTopColor: guardianColors['Main/Main'],
    borderRadius: scale(8),
    backgroundColor: guardianColors['Main/Main'],
  },
  tabletSettingsCard: { padding: 0, gap: 0, shadowOpacity: 0, elevation: 0 },
  tabletOrderCard: {
    shadowOpacity: 0,
    elevation: 0,
  },
  tabletSettingsRow: { height: scale(44), paddingHorizontal: scale(12) },
  selectedLabel: { color: colors['0'] },
  tabletSelectedRow: { borderRadius: scale(8), backgroundColor: '#E8F1FF' },
  tabletSelectedLabel: { color: guardianColors['10'] },
  pressed: { opacity: 0.68 },
}));
